import { useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchTeacherHome } from '../api/teacher'
import { today as todayLocal } from '../api/calendar'
import { fetchPortalDancer, fetchPortalHome } from '../api/portal'
import { useSession } from '../useSession'
import RosterModal from './RosterModal'
import TodayPanel from './TodayPanel'

/**
 * Fetches both halves of today and hands them to <see cref="TodayPanel"/>.
 *
 * **Split from the panel so the panel stays pure.** The join and the wording
 * are worth testing against Victoria Hogan's real Thursday without a query
 * client in the way; this part is four lines of plumbing and one decision.
 *
 * **The keys are the ones the portal already uses**, so opening Home warms the
 * schedule and vice versa rather than fetching the same evening twice. It also
 * means switching a dancer off invalidates this for free (D-381).
 */
export default function TodayForMe() {
  const { session } = useSession()
  const queryClient = useQueryClient()

  /*
   * The register she tapped, or null (D-780).
   *
   * **`RosterModal`, the same one `/schedule` opens** — the path the owner
   * reported as the one that works. Home had its own answer to "take
   * attendance" and it was a link to a list, which is the second
   * implementation A-17 forbids and the drift it predicts: the list holds
   * finished sessions only (D-685), so Home's copy could not reach a class she
   * was in the middle of teaching.
   *
   * **It lives here rather than in the panel** for the reason the split above
   * already gives: the panel is pure and testable against a real Thursday, and
   * everything holding a query client is this file's job.
   */
  const [attendanceFor, setAttendanceFor] = useState<string | null>(null)

  const teaches = session?.teaches === true

  /*
   * <b>Her own dancing counts, not only her children's</b> (D-572).
   *
   * This asked <c>isParent</c> alone, so a dancer with her own login had no
   * row here at all — and a student teacher who dances saw <i>You are
   * teaching: nothing today</i> above a blank space, while her Schedule tab
   * listed the five classes she is in. Reported from the deployed site.
   *
   * The portal payload is already hers to read: <c>/api/portal</c> builds from
   * the schedule scope, and a dancer's own id is in it (D-438, D-562). So this
   * is the one condition that was missing rather than a second request.
   */
  const hasDancing = session?.isParent === true || session?.isDancer === true

  const teacher = useQuery({
    queryKey: ['teacher-home'],
    queryFn: fetchTeacherHome,
    enabled: teaches,
  })

  const portal = useQuery({
    queryKey: ['portal'],
    queryFn: fetchPortalHome,
    enabled: hasDancing,
  })

  /*
   * One request per dancer, which is what the portal already does — and the
   * dancer endpoint is the only thing that carries her sessions. Same key, so
   * these are usually already in the cache by the time Home asks.
   */
  const dancers = useQueries({
    queries: (portal.data?.dancers ?? []).map((d) => ({
      queryKey: ['portal-dancer', d.dancerId],
      queryFn: () => fetchPortalDancer(d.dancerId),
    })),
  })

  /*
   * **The studio's today, from whichever payload answered** (D-68, D-384).
   *
   * The teacher query is disabled for a parent, so this used to fall through to
   * `new Date().toISOString()` for every family — a UTC date, and UTC rolls
   * over at 6-7pm Central, *inside* studio hours. It would have reported
   * tomorrow from seven o'clock on a class night, which is exactly when a
   * parent opens this. `api/calendar.ts` forbids `toISOString` here by name.
   *
   * It never bit because the panel was unreachable for a parent until D-384;
   * the portal payload carries `today` now so both halves read one clock.
   */
  const today = teacher.data?.today ?? portal.data?.today ?? todayLocal()

  /*
   * Waiting for every half that applies, rather than drawing one and then the
   * other — a row appearing a second after the rest reads as the screen
   * changing its mind, and this is the first thing on Home.
   */
  const waiting =
    (teaches && teacher.isPending) ||
    (hasDancing && (portal.isPending || dancers.some((q) => q.isPending)))

  /*
   * **Saying so, rather than rendering nothing.** This returned null while
   * pending, which is indistinguishable from having nothing to show — and on a
   * cold App Service start the sections below arrive first, so the screen looks
   * settled with the panel simply absent. Every database-backed endpoint
   * refuses for ~75 seconds after a deploy (D-33), which makes that the
   * ordinary case rather than a rare one.
   *
   * The same mistake as `DancerVisibilityPanel`, which a cold audit caught
   * hiding its own error state behind an early return (D-381).
   */
  if (waiting) {
    return (
      <section className="mb-5" aria-busy="true">
        <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Today</h2>
        <p className="rounded-xl border border-line bg-surface px-3 py-2.5 text-[13px] text-ink-3">
          Loading your day&hellip;
        </p>
      </section>
    )
  }

  /*
   * Both halves failed. Not the same as an empty day, and it must not read like
   * one — a screen that says nothing is on when it could not ask is the
   * untrue-statement-about-the-data D-209 exists to prevent.
   */
  const brokeDown = (teaches && teacher.isError) || (hasDancing && portal.isError)

  if (brokeDown) {
    return (
      <section className="mb-5">
        <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Today</h2>
        <p
          role="alert"
          className="rounded-xl border border-crit bg-crit-bg px-3 py-2.5 text-[13px]"
        >
          Today could not be loaded, so this is not a list of nothing. Try again in a moment.
        </p>
      </section>
    )
  }

  return (
    <>
    <TodayPanel
      today={today}
      teaches={teaches}
      onTakeAttendance={setAttendanceFor}
      // Today and the fortnight, so a row with nothing on can say what is next.
      teaching={[...(teacher.data?.todaySessions ?? []), ...(teacher.data?.upcoming ?? [])]}
      dancers={dancers
        .map((q) => q.data)
        .filter((d) => d !== undefined)
        .map((d) => ({
          name: d.name.split(' ')[0],
          // Her own row reads in the first person (D-572).
          self: d.dancerId === session?.dancerId,
          sessions: d.upcoming,
          // Her first class past the fortnight, when the fortnight is empty.
          startsOn: d.startsOn,
        }))}
    />

    {/*
      `kind="class"` takes a ClassOccurrence id, which is what `registerOf`
      hands back — the distinction that 404s when it is got wrong (D-316,
      D-733).

      **Closing it refreshes the day underneath** (D-780). The panel writes
      marks and invalidates the sheet; the row behind it is `['teacher-home']`,
      a different query, and this screen stays mounted the whole time — so
      without this she finishes a register, closes it, and Home still offers
      *Take attendance* on the class she has just marked. `staleTime` is 30s
      and `refetchOnWindowFocus` is off, so nothing else was ever going to ask
      again. From `/schedule` it did not show, because the Today panel is not
      on that screen.
    */}
    {attendanceFor && (
      <RosterModal
        kind="class"
        id={attendanceFor}
        onClose={() => {
          setAttendanceFor(null)
          void queryClient.invalidateQueries({ queryKey: ['teacher-home'] })
        }}
      />
    )}
    </>
  )
}
